import type { ReactNode } from 'react'
import { useOrganization } from '@clerk/clerk-react'
import { useQueryClient } from '@tanstack/react-query'

import { Badge } from '@/components/ui/badge'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { CapabilityGate } from '@/features/permissions/components/CapabilityGate'
import { workflowKeys } from '@/lib/queryKeys'
import { useWorkflowViews } from '../hooks/useWorkflowViews'
import { WorkflowListCard } from './WorkflowListCard'
import {
  CatalogueUnavailableState,
  EmptyState,
  ErrorState,
  NoOrgState,
  WorkflowCardSkeleton,
} from './WorkflowsSectionStates'

const SKELETON_KEYS = ['s1', 's2', 's3']

/**
 * The org's workflows, as a section of the Connections page.
 *
 * Workflows run against the connected systems listed above them, so they live
 * on that page rather than on one of their own. The capability gate is the same
 * one the retired `/workflows` route carried: `connections.status` admits
 * someone to the page, and it does not imply `workflows.list`.
 *
 * Gating here rather than inside keeps the hooks below unmounted for anyone
 * without the capability, so no workflow request is made for them at all.
 *
 * Bounded here too, for the same reason the section sits on someone else's
 * page: a throw in a workflow row must not take the credential status above it
 * down. The boundary is outside the content so a throw in the hooks is caught
 * as well as one in a card, and it draws the page's own furniture rather than
 * `ErrorBoundary`'s default — that fallback was written for a whole route, and
 * unstyled headings and a raw exception message read as a broken page when
 * they land halfway down a working one.
 */
export function WorkflowsSection() {
  return (
    <CapabilityGate capability="workflows.list">
      <ErrorBoundary
        fallback={(_error, resetError) => (
          <SectionFallback onDismiss={resetError} />
        )}>
        <WorkflowsSectionContent />
      </ErrorBoundary>
    </CapabilityGate>
  )
}

/**
 * What the boundary draws.
 *
 * Clearing the boundary is not enough on its own: a row the card cannot render
 * is served straight back from the query cache on the next pass and throws
 * again, so the button would redraw the same fallback and look dead. Dropping
 * the cached rows first makes it mean what it says — the refetch that follows
 * is the only way a different answer can arrive.
 */
function SectionFallback({ onDismiss }: { onDismiss: () => void }) {
  const queryClient = useQueryClient()
  const { organization } = useOrganization()

  return (
    <SectionShell>
      <ErrorState
        message="Couldn’t show workflows. The rest of this page is unaffected."
        label="Try again"
        onRetry={() => {
          void queryClient.resetQueries({
            queryKey: workflowKeys.all(organization?.id),
          })
          onDismiss()
        }}
      />
    </SectionShell>
  )
}

/**
 * The section's heading and rule, shared by its content and its fallback.
 *
 * The heading stays up when the list below it fails: a bare error where a
 * titled section used to be reads as a hole in the page.
 */
function SectionShell({
  children,
  action,
}: {
  children: ReactNode
  action?: ReactNode
}) {
  return (
    <section className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4 border-b border-border/60 pb-3">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-primary-high-contrast">
            Workflows
          </h2>
          <p className="text-sm text-muted-foreground">
            Automations that run against the connected systems above.
          </p>
        </div>
        {action}
      </header>
      {children}
    </section>
  )
}

function WorkflowsSectionContent() {
  const {
    data: workflows,
    isLoading,
    isError,
    isCatalogueUnavailable,
    hasNoOrg,
    refetch,
  } = useWorkflowViews()

  // Only from a settled, undegraded read. A count drawn while the catalogue is
  // missing or the list is still arriving describes what merged so far, not
  // what the org has.
  const showCount =
    !isLoading &&
    !isError &&
    !hasNoOrg &&
    !isCatalogueUnavailable &&
    workflows.length > 0

  function renderList() {
    // Before the loading branch: with no org there is nothing to wait for, and
    // the catalogue query stays pending forever rather than failing.
    if (hasNoOrg) {
      return <NoOrgState />
    }
    if (isLoading) {
      return SKELETON_KEYS.map((key) => <WorkflowCardSkeleton key={key} />)
    }
    if (isError) {
      return <ErrorState onRetry={() => refetch()} />
    }
    // Ahead of the empty branch, and shown alongside whatever did merge: with no
    // catalogue the list is empty today, and "no workflows defined" is the one
    // thing we know to be untrue.
    if (isCatalogueUnavailable) {
      return (
        <>
          <CatalogueUnavailableState onRetry={() => refetch()} />
          {workflows.map((workflow) => (
            <WorkflowListCard key={workflow.key} workflow={workflow} />
          ))}
        </>
      )
    }
    if (workflows.length === 0) {
      return <EmptyState />
    }
    return workflows.map((workflow) => (
      <WorkflowListCard key={workflow.key} workflow={workflow} />
    ))
  }

  return (
    <SectionShell
      action={
        showCount && (
          <Badge variant="outline" className="shrink-0 px-2.5 py-1">
            {workflows.length}{' '}
            {workflows.length === 1 ? 'workflow' : 'workflows'}
          </Badge>
        )
      }>
      <div className="flex flex-col gap-3">{renderList()}</div>
    </SectionShell>
  )
}
